import React from 'react';
import PropTypes from 'prop-types';
import withData from '@bd/bdcore-data';
import { get } from 'lodash';
import { GoogleMapUrl, Image } from '@bd/bdcore-utilities';
import { css } from '@bd/bdui-styles/emotion';
import { Link } from '@bd/bdcore-named-routes';
import numeral from 'numeral';
import moment from 'moment';
import { getPlanRouteProps } from '@bd/bdcore-data/build/Plans/selectors';

import { colors } from '../../styles';
import DetailHeaderWrapper from '../../components/DetailHeaderWrapper';
import ContactModal from '../../components/ContactModal';
import MortgageCalculatorModal from '../../components/MortgageCalculatorModal';

const styles = css`
  width: 100%;
  flex-direction: column;
  padding-top: 70px;
  @media(min-width: 992px) {
    display: flex;
    height: 800px;
    flex-direction: row;
    padding-top: 100px; /* for header area */
  }
  .DetailHeader_imageWrapper {
    width: 100%;
    height: 33vh;
    @media(min-width: 992px) {
      order: 1;
      flex: 1;
      height: 100%;
    }
  }
  .DetailHeader_contentWrapper {
    background-color: ${colors.neutral};
    @media(min-width: 992px) {
      width: 33.333vw;
      padding: 0 0 0 0; /* for descending logo */
    }
  }
  .DetailHeader_heading {
    background-color: ${colors.neutralDark};
    font-weight: 700;
    font-size: 20px;
    color: ${colors.brand1};
    margin: 0;
    padding: 20px 15px;
    line-height: 18px;
    @media(min-width: 992px) {
      font-size: 26px;
      padding: 20px 50px;
    }
    span {
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
      color: ${colors.body};
    }
  }
  .DetailHeader_price {
    background-color: ${colors.white};
    margin: 0;
    font-weight: 700;
    font-size: 24px;
    letter-spacing: 0.5px;
    padding: 5px 15px 10px 15px;
    color: ${colors.body};
    @media(min-width: 992px) {
      padding: 5px 10px 10px 50px;
    }
    span {
      font-weight: 700;
      font-size: 14px;
      letter-spacing: 0.5px;
    }
  }
  .DetailHeader_detailsList {
    list-style: none;
    margin: 15px 0 15px 0;
    @media(min-width: 992px) {
      padding: 0 50px;
    }
  }
  .DetailHeader_detailsListItem {
    font-size: 16px;
    line-height: 1.2;
    text-align: left;
    color: ${colors.body};
    font-weight: 300;
    margin: 0 0 6px 0;
  }
  .DetailHeader_iconList {
    display: flex;
    list-style: none;
    margin: 0;
    border-top: 1px solid ${colors.neutralDarkest};
    border-bottom: 1px solid ${colors.neutralDarkest};
    align-items: flex-start;
    padding: 0 50px;
    li {
      text-align: center;
      flex: 1;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      border-right: 1px solid ${colors.neutralDarkest};
      padding: 10px 5px;
      font-size: 15px;
      line-height: 1;
      &:first-child {
        border-left: 1px solid ${colors.neutralDarkest};
      }
      span {
        display: block;
      }
      img {
        height: 20px;
        margin: 0 0 5px 0;
      }
    }
  }
  .DetailHeader_links {
    padding: 10px 50px;
    display: flex;
    flex-direction: row;
    font-weight: 700;
    font-size: 14px;
    color: ${colors.brand1};
    text-transform: uppercase;
    @media(min-width: 992px) {
      padding: 10px 50px;
      flex-direction: row;
    }
    a, span {
      font-weight: bold;
      font-size: 14px;
      color: ${colors.brand1};
      letter-spacing: 1.85px;
      margin-bottom: 10px;
      text-transform: uppercase;
      margin: 10px;
      img {
        margin-right: 5px;
        height: 20px;
      }
    }
  }
  .DetailHeader_button {
    background-color: ${colors.brand1};
    padding: 0 30px 0 80px;
    color: ${colors.white};
    font-weight: 400;
    position: relative;
    line-height: 60px;
    display: inline-block;
    margin: 20px 0 20px 15px;
    transition: all .3s ease;
    cursor: pointer;
    &:hover {
      background-color: ${colors.brand1Lighter};
    }
    @media(min-width: 992px) {
      margin: 0 0 20px 50px;
    }
  }
  .DetailHeader_buttonImage {
    position: absolute;
    width: 60px;
    height: 60px;
    left: 0;
    top: 0;
    object-fit: cover;
  }
`;

const HomeHeader = ({ builder, community, home, plan, agent }) => {
  const garages = get(home, 'garages_range') || get(home, 'garages');
  const beds = get(home, 'beds_range') || get(home, 'beds');
  // const bathrooms = `${get(home, 'bathsFull')}
  // ${get(home, 'bathsThreeQuarter', '') && '.75'}
  // ${get(home, 'bathsHalf', '') && '.5'}`;
  return (
    <div className={styles}>
      <DetailHeaderWrapper imageUrl={get(home, 'photos[0].contentUrl')} className="DetailHeader_imageWrapper" >
        {
          get(home, 'headline') &&
          <div className="CommunityHeadline_wrapper">
            <div className="CommunityHeadline Promo_bar">
              {get(home, 'headline')}
            </div>
          </div>
        }
      </DetailHeaderWrapper>
      <div className="DetailHeader_contentWrapper">
        <h1 className="DetailHeader_heading">
          {get(home, 'address.streetAddress')}<br />
          <span>{get(home, 'address.addressLocality')}, {get(home, 'address.addressRegion')}, {get(home, 'address.postalCode')}</span>
        </h1>
        {get(home, 'price') > 0 &&
          <h3 className="DetailHeader_price">
            <span>Priced At</span><br />
            {numeral(get(home, 'price')).format('$0,0')}
          </h3>
        }
        {(!get(home, 'price') || get(home, 'price') === 0) &&
          <h3 className="DetailHeader_price py-3">
            Call for Details
          </h3>
        }
        <ul className="DetailHeader_detailsList">
          {
            get(home, 'moveInDate') &&
            moment(get(home, 'moveInDate')).isSameOrBefore(moment(), 'day') &&
            <li className="DetailHeader_detailsListItem">
              <b>Available Now</b>
            </li>
          }
          {
            get(home, 'moveInDate') &&
            moment(get(home, 'moveInDate')).isAfter(moment(), 'day') &&
            <li className="mb-3 DetailHeader_availability">
              <b>Available in {moment(get(home, 'moveInDate')).toNow('dd').replace('days', 'Days').replace('a day', '1 Day')}</b>
            </li>
          }
          {
            get(plan, 'name') &&
            <li className="DetailHeader_detailsListItem"><b>Floor Plan: </b><Link name="plan-detail" params={getPlanRouteProps(plan, { community })}>{get(plan, 'name')}</Link></li>
          }
          {
            get(community, 'name') &&
            <li className="DetailHeader_detailsListItem"><b>Community: </b><Link name="community-detail" community={community}>{get(community, 'name')}</Link></li>
          }
          {
            get(home, 'lot') &&
            <li className="DetailHeader_detailsListItem"><b>Lot: </b>{get(home, 'lot')}</li>
          }
        </ul>
        <ul className="DetailHeader_iconList">
          {
            beds &&
            <li>
              <img src="/images/icon-beds.svg" alt="Icon Beds" />
              <span><b>{beds}</b></span>
              <span>Beds</span>
            </li>
          }
          {
            get(home, 'custom_baths') &&
            <li>
              <img src="/images/icon-baths.svg" alt="Icon Baths" />
              <span><b>{get(home, 'custom_baths')}</b></span>
              <span>Baths</span>
            </li>
          }
          {/* {
            !get(home, 'custom_baths') &&
            <li>
              <img src="/images/icon-baths.svg" alt="Icon Baths" />
              <span><b>{bathrooms}</b></span>
              <span>Baths</span>
            </li>
          } */}
          {
            !get(home, 'custom_baths') &&
            <li>
              <img src="/images/icon-baths.svg" alt="Icon Baths" />
              <span className="d-flex">
                {
                  get(home, 'bathsFull', '') &&
                  <div><b>{get(home, 'bathsFull', '')}</b> Full</div>
                }
                {
                  get(home, 'bathsThreeQuarter') &&
                  <div className="px-2"><b>{get(home, 'bathsThreeQuarter', '')}</b> &frac34;</div>
                }
                {
                  get(home, 'bathsHalf', '') &&
                  <div><b>{get(home, 'bathsHalf', '')}</b> Half</div>
                }
              </span>
              <span>Baths</span>
            </li>
          }
          {
            get(home, 'sqft') &&
            <li>
              <img src="/images/icon-sqft.svg" alt="Icon Sqft" />
              <span><b>{numeral(get(home, 'sqft')).format('0,0')}</b></span>
              <span>SqFt</span>
            </li>
          }
          {
            garages &&
            <li>
              <img src="/images/icon-garages.svg" alt="Icon Garage" />
              <span><b>{garages}</b></span>
              <span>Cars</span>
            </li>
          }
        </ul>
        <div className="DetailHeader_links">
          <a href={GoogleMapUrl(home)} target="_blank" rel="noopener noreferrer"><img src="/images/icon-map-pin-black.svg" alt="Icon Get Directions" /> MAP</a>
          <span><MortgageCalculatorModal price={get(home, 'price')}><img src="/images/icon-calculator.svg" alt="Icon Get Directions" /> CALCULATOR</MortgageCalculatorModal></span>
        </div>
        <ContactModal builder={builder} formName="contactUsModalCommunity">
          <div className="DetailHeader_button">
            <Image
              src={get(agent, 'logo.contentUrl')}
              alt="Community Agent"
              className="DetailHeader_buttonImage"
              width={100}
              height={100}
            />
            Schedule A Showing
          </div>
        </ContactModal>
      </div>
    </div>
  );
};

HomeHeader.propTypes = {
  home: PropTypes.object.isRequired,
  builder: PropTypes.object.isRequired,
  community: PropTypes.object,
  plan: PropTypes.object,
  agent: PropTypes.object,
};

HomeHeader.defaultProps = {
  community: undefined,
  homes: [],
  plan: undefined,
  agent: undefined,
};

export default withData(
  {
    home: true,
    builder: true,
    community: true,
    plan: true,
    agent: true,
  },
  {
    required: {
      home: true,
    },
  },
)(HomeHeader);



// WEBPACK FOOTER //
// ./src/pages/HomeDetail/HomeHeader.js